import { formatCurrencyValue } from '@/modules/common/lib/formatters';
import { IUser, IUserWithProfile } from '@/types/user';
import AccountBalanceWalletOutlinedIcon from '@mui/icons-material/AccountBalanceWalletOutlined';
import DeleteOutlinedIcon from '@mui/icons-material/DeleteOutlined';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import VisibilityOutlinedIcon from '@mui/icons-material/VisibilityOutlined';
import {
  Button,
  ButtonGroup,
  Menu,
  MenuItem,
  Stack,
  Typography,
} from '@mui/material';
import { MRT_ColumnDef } from 'material-react-table';
import { useState } from 'react';

export type GetTeamMembersTableColumnsOpts = {
  learningBudgetLimit?: number;
  onSetLearningBudget?: (member: IUser) => void;
  onRemoveMember?: (member: IUser) => void;
};

export const getTeamMembersTableColumns = ({
  onSetLearningBudget,
  onRemoveMember,
}: GetTeamMembersTableColumnsOpts): MRT_ColumnDef<IUserWithProfile>[] => [
  {
    header: 'Name',
    accessorKey: 'lastName',
    Cell: ({ row: { original } }) => (
      <Stack>
        <Typography
          variant="body2"
          className="font-semibold"
          color="text.secondary"
        >
          {[original.firstName, original.lastName].filter(Boolean).join(' ')}
        </Typography>
        <Typography variant="body2">{original.email}</Typography>
      </Stack>
    ),
  },
  {
    header: 'Learning Budget',
    accessorKey: 'profile.learningBudgetBalance',
    Cell: ({ row: { original } }) => {
      const learningBudgetAmount = original.profile?.learningBudgetBalance ?? 0;

      return (
        <Stack
          flexDirection="row"
          alignItems="center"
          gap={'2px'}
          color={learningBudgetAmount ? 'text.primary' : 'text.secondary'}
        >
          <span className="text-sm">
            <AccountBalanceWalletOutlinedIcon
              fontSize="inherit"
              color="inherit"
            />
          </span>
          <Typography className="font-semibold" color="inherit">
            {formatCurrencyValue(
              original.profile?.learningBudgetBalance ?? 0,
              'eur',
            )}
          </Typography>
        </Stack>
      );
    },
  },
  {
    id: 'actions',
    header: 'Actions',
    grow: 0,
    Cell: ({ row: { original } }) => {
      const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
      const isOpen = Boolean(anchorEl);
      const showMenu = (event: React.MouseEvent<HTMLElement>) => {
        setAnchorEl(event.currentTarget);
      };
      const hideMenu = () => {
        setAnchorEl(null);
      };

      return (
        <>
          <ButtonGroup
            disableElevation
            variant="outlined"
            size="small"
            aria-label="member actions"
          >
            <Button
              href={`/employees/${original._id}`}
              variant="contained"
              className="rounded-r-none border-lines"
            >
              View
            </Button>
            <Button
              size="small"
              onClick={showMenu}
              aria-controls={isOpen ? 'member-actions-menu' : undefined}
              aria-haspopup="true"
              aria-expanded={isOpen ? 'true' : undefined}
              className="rounded-l-none bg-gray-background border-dividers"
            >
              <ExpandMoreIcon />
            </Button>
          </ButtonGroup>
          <Menu
            id={`member-actions-menu-${original._id}`}
            anchorEl={anchorEl}
            open={isOpen}
            onClose={hideMenu}
            slotProps={{
              list: {
                'aria-labelledby': 'member-actions-button',
              },
            }}
          >
            <MenuItem onClick={hideMenu}>
              <a
                href={`/employees/${original._id}`}
                className="flex items-center gap-2"
              >
                <VisibilityOutlinedIcon /> View member
              </a>
            </MenuItem>
            <MenuItem
              onClick={() => {
                hideMenu();
                onSetLearningBudget?.(original);
              }}
              className="flex items-center gap-2"
            >
              <AccountBalanceWalletOutlinedIcon /> Set learning budget
            </MenuItem>
            <MenuItem
              onClick={() => {
                hideMenu();
                onRemoveMember?.(original);
              }}
              className="flex items-center gap-2"
            >
              <DeleteOutlinedIcon /> Remove from team
            </MenuItem>
          </Menu>
        </>
      );
    },
  },
];
